import { memo } from "react";
import { View, StyleSheet, Platform, useWindowDimensions } from "react-native";
import { ProductCard } from "./ProductCard";
import { ProductEmptyState } from "./ProductEmptyState";
import { ProductLoadingState } from "./ProductLoadingState";
import { spacing } from "../../theme";
import type { Product } from "../../types/supabase";

// Max width for phone container on web (must match ProductCard)
const PHONE_CONTAINER_MAX_WIDTH = 430;

interface ProductGridProps {
  products?: Product[];
  isLoading?: boolean;
  onProductPress: (product: Product) => void;
  isSellerView?: boolean;
}

// Use memo to prevent unnecessary re-renders
export const ProductGrid = memo(function ProductGrid({
  products = [],
  isLoading,
  onProductPress,
  isSellerView = false,
}: ProductGridProps) {
  // Get responsive window dimensions (same as ProductCard)
  const windowDimensions = useWindowDimensions();
  const effectiveWidth = Platform.OS === 'web' 
    ? Math.min(windowDimensions.width, PHONE_CONTAINER_MAX_WIDTH)
    : windowDimensions.width;
  
  // Calculate card width (must match ProductCard calculation)
  const cardWidth = (effectiveWidth - spacing.lg * 2 - 4) / 2;

  if (isLoading) {
    return <ProductLoadingState />;
  }

  if (!products.length) {
    return (
      <ProductEmptyState
        title="No products found"
        description="Try adjusting your filters or check back later"
      />
    );
  }

  // Group products into pairs for 2-column grid
  const rows: Product[][] = [];
  for (let i = 0; i < products.length; i += 2) {
    rows.push(products.slice(i, i + 2));
  }

  return (
    <View style={styles.container}>
      {rows.map((row, index) => (
        <View key={index} style={styles.productsRow}>
          {row.map((product) => (
            <View key={product.id} style={styles.cardWrapper}>
              <ProductCard
                product={product}
                onPress={() => onProductPress(product)}
                isSellerView={isSellerView}
              />
            </View>
          ))}
          {/* Add invisible placeholder for odd rows to maintain left alignment */}
          {row.length === 1 && <View style={{ width: cardWidth }} />}
        </View>
      ))}
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    paddingVertical: 10,
    paddingHorizontal: spacing.lg,
  },
  productsRow: {
    flexDirection: "row",
    justifyContent: "center",
    marginBottom: spacing.sm,
    gap: 4,
  },
  cardWrapper: {
    // Width is controlled by ProductCard's calculatedCardWidth
  },
});
